import { Box, Typography, Container, Stack, Chip } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Work, Public, Storage, Accessibility, Stars } from '@mui/icons-material';

const Timeline = () => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ['start end', 'end start'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 1, 0.3]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);

  const milestones = [
    { 
      year: '1998', 
      title: 'Founded First Venture', 
      description: 'Launched initial business operations in South Africa, establishing foundational success.',
      icon: <Work />,
      color: '#C8A96B'
    },
    { 
      year: '2005', 
      title: 'International Expansion', 
      description: 'Extended operations to European and Asian markets, building global presence.',
      icon: <Public />,
      color: '#C8A96B'
    },
    { 
      year: '2012', 
      title: 'Global Infrastructure', 
      description: 'Established worldwide logistics and communication networks across 30+ countries.',
      icon: <Storage />,
      color: '#C8A96B'
    },
    { 
      year: '2018', 
      title: 'Direct Access Initiative', 
      description: 'Launched the "No Gatekeepers" platform, revolutionizing executive accessibility.',
      icon: <Accessibility />,
      color: '#C8A96B'
    },
    { 
      year: '2024', 
      title: 'Global Leadership', 
      description: 'Recognized as a pioneer in executive accessibility and transparent communication.',
      icon: <Stars />,
      color: '#C8A96B'
    },
  ];

  return (
    <Box 
      ref={targetRef} 
      sx={{ 
        py: { xs: 10, md: 18 }, 
        background: 'linear-gradient(180deg, #0a0a0a 0%, #0f0f0f 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <motion.div style={{ opacity, scale }}>
          <Typography
            variant="subtitle1"
            sx={{ 
              color: '#C8A96B', 
              letterSpacing: 6, 
              mb: 2, 
              textAlign: 'center',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            CAREER MILESTONES
          </Typography>

          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              fontSize: { xs: '2.2rem', sm: '3rem', md: '3.8rem' },
              fontWeight: 800,
              background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            The Journey
          </Typography>

          <Typography
            sx={{
              textAlign: 'center',
              maxWidth: '600px',
              mx: 'auto',
              color: '#aaa',
              mb: 10,
              fontSize: '1rem',
            }}
          >
            Two decades of transformative leadership and global impact
          </Typography>
        </motion.div>

        <Stack spacing={5} sx={{ position: 'relative' }}>
          {/* Vertical Timeline Line - Desktop Only */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '2px',
              height: '90%',
              background: 'linear-gradient(to bottom, #C8A96B, rgba(200,169,107,0.2), #C8A96B)',
              display: { xs: 'none', md: 'block' },
            }}
          />

          {milestones.map((milestone, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: index * 0.15, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: index % 2 === 0 ? 'flex-start' : 'flex-end',
                  alignItems: 'center',
                  gap: 3,
                  position: 'relative',
                }}
              >
                {/* Timeline Node */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: { md: '50%' },
                    transform: { md: 'translateX(-50%)' },
                    width: { xs: '100%', md: 'auto' },
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 2,
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.4 }}
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: '#111',
                      border: `3px solid ${milestone.color}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: milestone.color,
                      boxShadow: '0 0 20px rgba(200,169,107,0.3)',
                    }}
                  >
                    {milestone.icon}
                  </motion.div>
                </Box>

                {/* Content Box */}
                <Box
                  sx={{
                    width: { xs: '100%', md: '45%' },
                    ml: { md: index % 2 === 0 ? 0 : 'auto' },
                    mr: { md: index % 2 === 0 ? 'auto' : 0 },
                    background: 'linear-gradient(135deg, #111 0%, #161616 100%)',
                    borderRadius: '20px',
                    p: 3.5,
                    border: '1px solid rgba(200,169,107,0.15)',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#C8A96B',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                    },
                  }}
                >
                  <Chip
                    label={milestone.year}
                    size="small"
                    sx={{
                      background: 'rgba(200,169,107,0.15)',
                      color: '#C8A96B',
                      fontWeight: 600,
                      mb: 2,
                      letterSpacing: 1,
                    }}
                  />
                  <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700, color: '#fff' }}>
                    {milestone.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#aaa', lineHeight: 1.6 }}>
                    {milestone.description}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default Timeline;